import React, { useState } from 'react';
import {
  Title,
  Text,
  Stack,
  Tabs,
  TextInput,
  Select,
  Switch,
  Button,
  Group,
  Card,
  SimpleGrid,
  NumberInput,
  Textarea,
  ActionIcon,
  Badge,
  Alert,
  ColorInput,
  MultiSelect,
  PasswordInput,
  Divider,
} from '@mantine/core';
import {
  IconSettings,
  IconBell,
  IconShield,
  IconPalette,
  IconDatabase,
  IconMail,
  IconCheck,
  IconRefresh,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string | null>('general');
  const [saving, setSaving] = useState(false);

  // Form states
  const [generalSettings, setGeneralSettings] = useState({
    systemName: 'YochienAI',
    adminEmail: 'admin@yochienai.com',
    supportEmail: 'support@yochienai.com',
    timezone: 'Asia/Tokyo',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    alertSound: true,
    alertTypes: ['safety', 'attendance', 'activity'],
    notificationFrequency: 'realtime',
  });

  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    sessionTimeout: 30,
    passwordExpiry: 90,
    minPasswordLength: 8,
    requireSpecialChars: true,
    requireNumbers: true,
    maxLoginAttempts: 5,
    ipWhitelist: [] as string[],
  });

  const handleSave = async (section: string) => {
    setSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    
    notifications.show({
      title: 'Settings Saved',
      message: `${section} settings have been updated successfully`,
      color: 'green',
      icon: <IconCheck size={18} />,
    });
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={2}>System Settings</Title>
          <Text size="sm" c="dimmed">
            Configure system-wide settings and preferences
          </Text>
        </div>
        <Button
          leftSection={<IconRefresh size={16} />}
          variant="light"
          onClick={() => window.location.reload()}
        >
          Reset All
        </Button>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="general" leftSection={<IconSettings size={16} />}>
            General
          </Tabs.Tab>
          <Tabs.Tab value="notifications" leftSection={<IconBell size={16} />}>
            Notifications
          </Tabs.Tab>
          <Tabs.Tab value="security" leftSection={<IconShield size={16} />}>
            Security
          </Tabs.Tab>
          <Tabs.Tab value="appearance" leftSection={<IconPalette size={16} />}>
            Appearance
          </Tabs.Tab>
          <Tabs.Tab value="integrations" leftSection={<IconDatabase size={16} />}>
            Integrations
          </Tabs.Tab>
          <Tabs.Tab value="email" leftSection={<IconMail size={16} />}>
            Email
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="general" pt="lg">
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>General Settings</Title>
              
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                <TextInput
                  label="System Name"
                  placeholder="Enter system name"
                  value={generalSettings.systemName}
                  onChange={(e) => setGeneralSettings({
                    ...generalSettings,
                    systemName: e.target.value
                  })}
                />
                <Select
                  label="Default Language"
                  placeholder="Select language"
                  data={[
                    { value: 'en', label: 'English' },
                    { value: 'ja', label: 'Japanese' },
                    { value: 'ko', label: 'Korean' },
                    { value: 'zh', label: 'Chinese' },
                  ]}
                  value={generalSettings.language}
                  onChange={(value) => setGeneralSettings({
                    ...generalSettings,
                    language: value || 'en'
                  })}
                />
                <TextInput
                  label="Admin Email"
                  placeholder="admin@example.com"
                  value={generalSettings.adminEmail}
                  onChange={(e) => setGeneralSettings({
                    ...generalSettings,
                    adminEmail: e.target.value
                  })}
                />
                <TextInput
                  label="Support Email"
                  placeholder="support@example.com"
                  value={generalSettings.supportEmail}
                  onChange={(e) => setGeneralSettings({
                    ...generalSettings,
                    supportEmail: e.target.value
                  })}
                />
                <Select
                  label="Timezone"
                  placeholder="Select timezone"
                  data={[
                    { value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
                    { value: 'America/New_York', label: 'America/New York' },
                    { value: 'Europe/London', label: 'Europe/London' },
                    { value: 'Australia/Sydney', label: 'Australia/Sydney' },
                  ]}
                  value={generalSettings.timezone}
                  onChange={(value) => setGeneralSettings({
                    ...generalSettings,
                    timezone: value || 'Asia/Tokyo'
                  })}
                />
                <Select
                  label="Date Format"
                  placeholder="Select date format"
                  data={[
                    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
                  ]}
                  value={generalSettings.dateFormat}
                  onChange={(value) => setGeneralSettings({
                    ...generalSettings,
                    dateFormat: value || 'MM/DD/YYYY'
                  })}
                />
              </SimpleGrid>

              <Group justify="flex-end" mt="md">
                <Button
                  loading={saving}
                  onClick={() => handleSave('General')}
                >
                  Save General Settings
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="notifications" pt="lg">
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>Notification Preferences</Title>
              
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                <Switch
                  label="Email Notifications"
                  description="Receive notifications via email"
                  checked={notificationSettings.emailNotifications}
                  onChange={(e) => setNotificationSettings({
                    ...notificationSettings,
                    emailNotifications: e.currentTarget.checked
                  })}
                />
                <Switch
                  label="SMS Notifications"
                  description="Receive notifications via SMS"
                  checked={notificationSettings.smsNotifications}
                  onChange={(e) => setNotificationSettings({
                    ...notificationSettings,
                    smsNotifications: e.currentTarget.checked
                  })}
                />
                <Switch
                  label="Push Notifications"
                  description="Receive browser push notifications"
                  checked={notificationSettings.pushNotifications}
                  onChange={(e) => setNotificationSettings({
                    ...notificationSettings,
                    pushNotifications: e.currentTarget.checked
                  })}
                />
                <Switch
                  label="Alert Sounds"
                  description="Play sound for important alerts"
                  checked={notificationSettings.alertSound}
                  onChange={(e) => setNotificationSettings({
                    ...notificationSettings,
                    alertSound: e.currentTarget.checked
                  })}
                />
              </SimpleGrid>

              <MultiSelect
                label="Alert Types"
                description="Select which types of alerts to receive"
                data={[
                  { value: 'safety', label: 'Safety Alerts' },
                  { value: 'attendance', label: 'Attendance Alerts' },
                  { value: 'activity', label: 'Activity Updates' },
                  { value: 'system', label: 'System Notifications' },
                  { value: 'report', label: 'Report Notifications' },
                ]}
                value={notificationSettings.alertTypes}
                onChange={(value) => setNotificationSettings({
                  ...notificationSettings,
                  alertTypes: value
                })}
              />

              <Select
                label="Notification Frequency"
                description="How often to receive batch notifications"
                data={[
                  { value: 'realtime', label: 'Real-time' },
                  { value: 'hourly', label: 'Hourly Summary' },
                  { value: 'daily', label: 'Daily Digest' },
                  { value: 'weekly', label: 'Weekly Summary' },
                ]}
                value={notificationSettings.notificationFrequency}
                onChange={(value) => setNotificationSettings({
                  ...notificationSettings,
                  notificationFrequency: value || 'realtime'
                })}
              />

              <Group justify="flex-end" mt="md">
                <Button
                  loading={saving}
                  onClick={() => handleSave('Notification')}
                >
                  Save Notification Settings
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="security" pt="lg">
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>Security Settings</Title>
              
              <Alert color="yellow" icon={<IconShield size={16} />}>
                Changing security settings may affect user access. Please review carefully before saving.
              </Alert>

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                <Switch
                  label="Two-Factor Authentication"
                  description="Require 2FA for all admin users"
                  checked={securitySettings.twoFactorAuth}
                  onChange={(e) => setSecuritySettings({
                    ...securitySettings,
                    twoFactorAuth: e.currentTarget.checked
                  })}
                />
                <NumberInput
                  label="Session Timeout (minutes)"
                  description="Auto-logout after inactivity"
                  min={5}
                  max={120}
                  value={securitySettings.sessionTimeout}
                  onChange={(value) => setSecuritySettings({
                    ...securitySettings,
                    sessionTimeout: value !== undefined && value !== null ? Number(value) : 30
                  })}
                />
                <NumberInput
                  label="Password Expiry (days)"
                  description="Force password change after days"
                  min={0}
                  max={365}
                  value={securitySettings.passwordExpiry}
                  onChange={(value) => setSecuritySettings({
                    ...securitySettings,
                    passwordExpiry: value !== undefined && value !== null ? Number(value) : 90
                  })}
                />
                <NumberInput
                  label="Minimum Password Length"
                  min={6}
                  max={32}
                  value={securitySettings.minPasswordLength}
                  onChange={(value) => setSecuritySettings({
                    ...securitySettings,
                    minPasswordLength: value !== undefined && value !== null ? Number(value) : 8
                  })}
                />
                <Switch
                  label="Require Special Characters"
                  description="Passwords must include special characters"
                  checked={securitySettings.requireSpecialChars}
                  onChange={(e) => setSecuritySettings({
                    ...securitySettings,
                    requireSpecialChars: e.currentTarget.checked
                  })}
                />
                <Switch
                  label="Require Numbers"
                  description="Passwords must include numbers"
                  checked={securitySettings.requireNumbers}
                  onChange={(e) => setSecuritySettings({
                    ...securitySettings,
                    requireNumbers: e.currentTarget.checked
                  })}
                />
              </SimpleGrid>

              <Divider />

              <Stack gap="sm">
                <Text fw={500}>IP Whitelist</Text>
                <Text size="sm" c="dimmed">
                  Only allow access from these IP addresses (leave empty to allow all)
                </Text>
                {securitySettings.ipWhitelist.map((ip, index) => (
                  <Group key={index}>
                    <TextInput
                      value={ip}
                      placeholder="192.168.1.1"
                      style={{ flex: 1 }}
                      readOnly
                    />
                    <ActionIcon
                      color="red"
                      variant="light"
                      onClick={() => {
                        const newList = [...securitySettings.ipWhitelist];
                        newList.splice(index, 1);
                        setSecuritySettings({
                          ...securitySettings,
                          ipWhitelist: newList
                        });
                      }}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                ))}
                <Button
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => {
                    const ip = prompt('Enter IP address:');
                    if (ip) {
                      setSecuritySettings({
                        ...securitySettings,
                        ipWhitelist: [...securitySettings.ipWhitelist, ip]
                      });
                    }
                  }}
                >
                  Add IP Address
                </Button>
              </Stack>

              <Group justify="flex-end" mt="md">
                <Button
                  loading={saving}
                  onClick={() => handleSave('Security')}
                >
                  Save Security Settings
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="appearance" pt="lg">
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>Appearance Settings</Title>
              
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                <ColorInput
                  label="Primary Color"
                  placeholder="Pick color"
                  defaultValue="#228be6"
                />
                <ColorInput
                  label="Secondary Color"
                  placeholder="Pick color"
                  defaultValue="#40c057"
                />
                <Select
                  label="Theme Mode"
                  data={[
                    { value: 'light', label: 'Light' },
                    { value: 'dark', label: 'Dark' },
                    { value: 'auto', label: 'Auto (System)' },
                  ]}
                  defaultValue="light"
                />
                <Select
                  label="Font Family"
                  data={[
                    { value: 'system', label: 'System Default' },
                    { value: 'inter', label: 'Inter' },
                    { value: 'roboto', label: 'Roboto' },
                    { value: 'opensans', label: 'Open Sans' },
                  ]}
                  defaultValue="system"
                />
              </SimpleGrid>

              <Textarea
                label="Custom CSS"
                description="Add custom CSS to override default styles"
                placeholder="/* Your custom CSS here */"
                rows={6}
              />

              <Group justify="flex-end" mt="md">
                <Button
                  loading={saving}
                  onClick={() => handleSave('Appearance')}
                >
                  Save Appearance Settings
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="integrations" pt="lg">
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>Third-Party Integrations</Title>
              
              <SimpleGrid cols={1} spacing="md">
                <Card withBorder>
                  <Group justify="space-between" mb="md">
                    <div>
                      <Text fw={500}>Google Calendar</Text>
                      <Text size="sm" c="dimmed">
                        Sync events and schedules with Google Calendar
                      </Text>
                    </div>
                    <Badge color="green">Connected</Badge>
                  </Group>
                  <Group>
                    <Button size="sm" variant="light">Configure</Button>
                    <Button size="sm" variant="light" color="red">Disconnect</Button>
                  </Group>
                </Card>

                <Card withBorder>
                  <Group justify="space-between" mb="md">
                    <div>
                      <Text fw={500}>Slack</Text>
                      <Text size="sm" c="dimmed">
                        Send notifications to Slack channels
                      </Text>
                    </div>
                    <Badge color="gray">Not Connected</Badge>
                  </Group>
                  <Button size="sm">Connect</Button>
                </Card>

                <Card withBorder>
                  <Group justify="space-between" mb="md">
                    <div>
                      <Text fw={500}>SMS Gateway</Text>
                      <Text size="sm" c="dimmed">
                        Send SMS notifications to parents
                      </Text>
                    </div>
                    <Badge color="gray">Not Connected</Badge>
                  </Group>
                  <Button size="sm">Configure</Button>
                </Card>
              </SimpleGrid>
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="email" pt="lg">
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>Email Configuration</Title>
              
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                <TextInput
                  label="SMTP Server"
                  placeholder="smtp.gmail.com"
                />
                <NumberInput
                  label="SMTP Port"
                  placeholder="587"
                  min={1}
                  max={65535}
                />
                <TextInput
                  label="SMTP Username"
                  placeholder="your-email@gmail.com"
                />
                <PasswordInput
                  label="SMTP Password"
                  placeholder="Enter password"
                />
                <Select
                  label="Encryption"
                  data={[
                    { value: 'tls', label: 'TLS' },
                    { value: 'ssl', label: 'SSL' },
                    { value: 'none', label: 'None' },
                  ]}
                  defaultValue="tls"
                />
                <TextInput
                  label="From Email"
                  placeholder="noreply@yochienai.com"
                />
              </SimpleGrid>

              <Textarea
                label="Email Signature"
                placeholder="Enter email signature template"
                rows={4}
              />

              <Group justify="space-between" mt="md">
                <Button variant="light">Send Test Email</Button>
                <Button
                  loading={saving}
                  onClick={() => handleSave('Email')}
                >
                  Save Email Settings
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};

export default SettingsPage;